import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminBlogs() {
  const [items, setItems] = useState([]);
  const [images, setImages] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/blogs").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/images").then((r) => setImages(r.data)); }, []);

  const save = async () => {
    try {
      const isNew = !edit.id;
      const payload = { slug: edit.slug, title: edit.title, excerpt: edit.excerpt || "",
        body: edit.body || "", cover_image_id: edit.cover_image_id || null,
        tags: (edit.tags || "").split(",").map(s => s.trim()).filter(Boolean),
        published: edit.published !== false };
      if (isNew) await api.post("/admin/blogs", payload);
      else await api.patch(`/admin/blogs/${edit.id}`, payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/blogs/${id}`); load(); };

  return (
    <div data-testid="admin-blogs">
      <div className="flex items-end justify-between border-b border-border pb-4 mb-6">
        <div><h1 className="font-display text-4xl tracking-tight">Blog posts</h1>
          <p className="text-sm text-muted-foreground italic mt-1">Letters, essays and announcements from the press.</p></div>
        <Button onClick={() => setEdit({ slug: "", title: "", body: "", excerpt: "", tags: "", published: true })} className="rounded-sm" data-testid="new-blog"><Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />New post</Button>
      </div>

      <div className="border border-border rounded-sm bg-card divide-y divide-border">
        {items.length === 0 && <div className="px-4 py-10 text-center text-sm text-muted-foreground italic">No posts yet.</div>}
        {items.map((b) => (
          <div key={b.id} className="flex items-center justify-between px-4 py-3" data-testid={`blog-row-${b.id}`}>
            <div>
              <div className="font-display text-lg">{b.title} {!b.published && <span className="text-xs text-muted-foreground italic ml-2">(draft)</span>}</div>
              <div className="text-xs text-muted-foreground font-mono">/{b.slug} · {b.created_at?.slice(0, 10)}</div>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setEdit({ ...b, tags: (b.tags || []).join(", ") })}>Edit</Button>
              <Button variant="ghost" size="sm" onClick={() => remove(b.id)}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm">
            <DialogHeader><DialogTitle className="font-display text-2xl">{edit.id ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Slug</Label><Input value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} className="rounded-sm mt-1.5 font-mono" data-testid="bl-slug" /></div>
                <div><Label>Title</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="rounded-sm mt-1.5" data-testid="bl-title" /></div>
              </div>
              <div><Label>Excerpt</Label><Textarea rows={2} value={edit.excerpt} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} className="rounded-sm mt-1.5" data-testid="bl-excerpt" /></div>
              <div><Label>Body</Label><Textarea rows={14} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className="rounded-sm mt-1.5 font-mono text-sm" data-testid="bl-body" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tags (comma)</Label><Input value={edit.tags} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} className="rounded-sm mt-1.5" data-testid="bl-tags" /></div>
                <div>
                  <Label>Cover image</Label>
                  <Select value={edit.cover_image_id || "none"} onValueChange={(v) => setEdit({ ...edit, cover_image_id: v === "none" ? null : v })}>
                    <SelectTrigger className="rounded-sm mt-1.5"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {images.map((i) => <SelectItem key={i.id} value={i.id}>{i.filename}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={edit.published !== false} onCheckedChange={(v) => setEdit({ ...edit, published: v })} id="bl-pub" /><Label htmlFor="bl-pub">Published</Label></div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} data-testid="bl-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
